import { useState, useEffect, useCallback, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import api from '@/lib/api';
import { useAuth } from '@/context/AuthContext';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger } from '@/components/ui/dropdown-menu';
import { Wallet, Plus, MoreHorizontal, Pencil, Trash2, ExternalLink, ArrowUp, ArrowDown, ArrowUpDown, Users } from 'lucide-react';
import { toast } from 'sonner';
import { ExpenseDialog } from '@/components/expenses/ExpenseDialog';
import { BeneficiaryDetailDialog } from '@/components/expenses/BeneficiaryDetailDialog';

const TYPE_COLORS = {
  permit: 'bg-violet-50 text-violet-700',
  fuel: 'bg-amber-50 text-amber-700',
  labor: 'bg-blue-50 text-blue-700',
  material: 'bg-emerald-50 text-emerald-700',
  subcontractor: 'bg-cyan-50 text-cyan-700',
  other: 'bg-slate-100 text-slate-600',
};

const STATUS_FILTERS = ['all', 'unpaid', 'paid', 'unset'];

function SortHeader({ label, sortKey, activeKey, dir, onSort, className = '', testId }) {
  const active = activeKey === sortKey;
  return (
    <th className={`text-xs uppercase tracking-wider text-gray-500 font-semibold px-4 py-3 ${className}`}>
      <button type="button" onClick={() => onSort(sortKey)} className="flex items-center gap-1 hover:text-gray-800" data-testid={testId}>
        {label}
        {active ? (dir === 'asc' ? <ArrowUp className="w-3 h-3" /> : <ArrowDown className="w-3 h-3" />) : <ArrowUpDown className="w-3 h-3 opacity-30" />}
      </button>
    </th>
  );
}

export default function ExpensesPage() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { hasPermission } = useAuth();
  const canCreate = hasPermission('expenses', 'create');
  const canEdit = hasPermission('expenses', 'edit');
  const canDelete = hasPermission('expenses', 'delete');
  const canViewAll = hasPermission('view_all_expenses');
  const [view, setView] = useState('all'); // 'all' | 'by_beneficiary'
  const [expenses, setExpenses] = useState([]);
  const [projects, setProjects] = useState([]);
  const [loading, setLoading] = useState(true);
  const [projectFilter, setProjectFilter] = useState('all');
  const [statusFilter, setStatusFilter] = useState('all');
  const [sortKey, setSortKey] = useState('date');
  const [sortDir, setSortDir] = useState('desc');
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [byBeneficiary, setByBeneficiary] = useState({ beneficiaries: [], total_unpaid: 0, total_paid: 0, total_unset: 0, total: 0 });
  const [byBeneficiaryLoading, setByBeneficiaryLoading] = useState(false);
  const [detailBeneficiary, setDetailBeneficiary] = useState(null);

  const fetchData = useCallback(async () => {
    setLoading(true);
    try {
      const params = {};
      if (projectFilter !== 'all') params.project_id = projectFilter;
      const res = await api.get('/expenses', { params });
      setExpenses(res.data || []);
    } catch { toast.error(t('errors.loadingError')); }
    finally { setLoading(false); }
  }, [projectFilter, t]);

  const fetchByBeneficiary = useCallback(async () => {
    setByBeneficiaryLoading(true);
    try {
      const res = await api.get('/expenses/by-beneficiary');
      setByBeneficiary(res.data || { beneficiaries: [], total_unpaid: 0, total_paid: 0, total_unset: 0, total: 0 });
    } catch { toast.error(t('errors.loadingError')); }
    finally { setByBeneficiaryLoading(false); }
  }, [t]);

  useEffect(() => { fetchData(); }, [fetchData]);
  useEffect(() => { if (view === 'by_beneficiary' && canViewAll) fetchByBeneficiary(); }, [view, canViewAll, fetchByBeneficiary]);
  useEffect(() => {
    api.get('/projects', { params: { page_size: 500 } }).then(r => setProjects(r.data.items || r.data || [])).catch(() => {});
  }, []);

  // Fix O (2026-09-10) — Medido ANTES de tocar nada: la formula vieja del
  // backend (/expenses/by-beneficiary) usaba $ne (payment_status != 'paid')
  // para "unpaid_total", lo que YA sumaba correctamente contra el total
  // (paid_total + unpaid_total == total, sin gastos perdidos ni duplicados) —
  // no era un bug de matematica. El problema real era de PRESENTACION: los
  // gastos SIN payment_status quedaban disimulados dentro de "unpaid" (Alian:
  // "no me acaba de hacer sutil... no disimules"), y no habia ninguna tarjeta
  // de TOTAL en esta pantalla — solo un texto chico "Total: $X" sin desglose,
  // lo que probablemente explica ver el "mismo numero" en más de un lugar.
  // Ahora se separan 3 baldes reales (paid/unpaid/sin estado) que SIEMPRE
  // suman el total exacto, por construccion (particion completa del array).
  const totals = useMemo(() => {
    let paid = 0, unpaid = 0, unset = 0;
    for (const e of expenses) {
      const amt = e.amount || 0;
      if (e.payment_status === 'paid') paid += amt;
      else if (e.payment_status === 'unpaid') unpaid += amt;
      else unset += amt;
    }
    return { paid, unpaid, unset, total: paid + unpaid + unset };
  }, [expenses]);
  const total = totals.total;

  const openCreate = () => { setEditing(null); setDialogOpen(true); };
  const openEdit = (e) => { setEditing(e); setDialogOpen(true); };
  const handleDelete = async (id) => {
    try { await api.delete(`/expenses/${id}`); toast.success(t('success.deleted')); fetchData(); }
    catch { toast.error(t('errors.deletingError')); }
  };
  const handleSaved = () => { fetchData(); if (view === 'by_beneficiary') fetchByBeneficiary(); };

  const typeLabel = (ty) => t(`expenses.types.${ty}`, { defaultValue: ty ? ty.charAt(0).toUpperCase() + ty.slice(1) : '' });

  const handleSort = (key) => {
    if (sortKey === key) setSortDir(d => (d === 'asc' ? 'desc' : 'asc'));
    else { setSortKey(key); setSortDir('asc'); }
  };

  const visibleExpenses = useMemo(() => {
    let rows = expenses;
    if (statusFilter !== 'all') rows = rows.filter(e => (e.payment_status || 'unset') === statusFilter);
    const dir = sortDir === 'asc' ? 1 : -1;
    const val = (e) => {
      switch (sortKey) {
        case 'beneficiary': return (e.beneficiary_name || '').toLowerCase();
        case 'project': return (e.project_number || '').toLowerCase();
        case 'amount': return e.amount || 0;
        case 'status': return e.payment_status || 'unset';
        default: return e.date || '';
      }
    };
    return [...rows].sort((a, b) => {
      const va = val(a), vb = val(b);
      if (va < vb) return -1 * dir;
      if (va > vb) return 1 * dir;
      return 0;
    });
  }, [expenses, statusFilter, sortKey, sortDir]);

  return (
    <div data-testid="expenses-page" className="space-y-4 sm:space-y-6">
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3">
        <div>
          <h1 className="text-xl sm:text-2xl font-bold text-gray-900 font-['Barlow'] tracking-tight flex items-center gap-2">
            <Wallet className="w-5 h-5 sm:w-6 sm:h-6 text-[#3399FF]" /> {t('expenses.title')}
          </h1>
          <p className="text-xs sm:text-sm text-gray-500 mt-1">{t('expenses.subtitle')}</p>
        </div>
        {canCreate && (
          <Button data-testid="new-expense-btn" onClick={openCreate} size="sm" className="bg-[#003366] hover:bg-[#004080] text-white shadow-sm self-start sm:self-auto">
            <Plus className="w-4 h-4 mr-2" /> {t('expenses.newExpense')}
          </Button>
        )}
      </div>

      {/* Alian: separar la tabla de gastos (operativa) de la vista de "a quien le debo" (consulta) */}
      <div className="flex gap-1.5 border-b border-gray-200">
        <button type="button" onClick={() => setView('all')} data-testid="expenses-tab-all"
          className={`px-3.5 py-2 text-sm font-medium border-b-2 transition-colors ${view === 'all' ? 'border-[#003366] text-[#003366]' : 'border-transparent text-gray-500 hover:text-gray-800'}`}>
          {t('expenses.allExpenses')}
        </button>
        {canViewAll && (
          <button type="button" onClick={() => setView('by_beneficiary')} data-testid="expenses-tab-by-beneficiary"
            className={`px-3.5 py-2 text-sm font-medium border-b-2 flex items-center gap-1.5 transition-colors ${view === 'by_beneficiary' ? 'border-[#003366] text-[#003366]' : 'border-transparent text-gray-500 hover:text-gray-800'}`}>
            <Users className="w-3.5 h-3.5" /> {t('expenses.byBeneficiary')}
          </button>
        )}
      </div>

      {view === 'all' ? (
        <>
          {/* Fix O — CAMBIO 4: tarjetas simples y sobrias, una etiqueta clara por número */}
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
            <div className="bg-white border border-gray-200 rounded-xl p-3.5" data-testid="expenses-kpi-total">
              <p className="text-[11px] font-semibold text-gray-500 uppercase tracking-wide">{t('expenses.totalAll')}</p>
              <p className="text-xl font-bold text-gray-900 mt-1">${totals.total.toFixed(2)}</p>
            </div>
            <div className="bg-emerald-50 border border-emerald-200 rounded-xl p-3.5" data-testid="expenses-kpi-paid">
              <p className="text-[11px] font-semibold text-emerald-700 uppercase tracking-wide">{t('expenses.totalPaidLabel')}</p>
              <p className="text-xl font-bold text-emerald-800 mt-1">${totals.paid.toFixed(2)}</p>
            </div>
            <div className="bg-amber-50 border border-amber-200 rounded-xl p-3.5" data-testid="expenses-kpi-unpaid">
              <p className="text-[11px] font-semibold text-amber-700 uppercase tracking-wide">{t('expenses.totalOwed')}</p>
              <p className="text-xl font-bold text-amber-800 mt-1">${totals.unpaid.toFixed(2)}</p>
            </div>
            <div className="bg-gray-50 border border-gray-200 rounded-xl p-3.5" data-testid="expenses-kpi-unset">
              <p className="text-[11px] font-semibold text-gray-500 uppercase tracking-wide">{t('expenses.totalNotSet')}</p>
              <p className="text-xl font-bold text-gray-600 mt-1">${totals.unset.toFixed(2)}</p>
            </div>
          </div>

          <div className="flex flex-col sm:flex-row gap-3 sm:items-center">
            <Select value={projectFilter} onValueChange={setProjectFilter}>
              <SelectTrigger data-testid="expense-project-filter" className="w-full sm:w-72 h-9 bg-white"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="all">{t('expenses.allProjects')}</SelectItem>
                {projects.map(p => <SelectItem key={p.id} value={p.id}>{p.project_number} — {p.title}</SelectItem>)}
              </SelectContent>
            </Select>
            <div className="flex rounded-lg border border-gray-200 overflow-hidden bg-white">
              {STATUS_FILTERS.map(s => (
                <button key={s} type="button" onClick={() => setStatusFilter(s)} data-testid={`expense-status-filter-${s}`}
                  className={`px-3 h-9 text-sm font-medium transition-colors ${statusFilter === s ? 'bg-[#003366] text-white' : 'text-gray-600 hover:bg-gray-50'}`}>
                  {t(`expenses.filter${s.charAt(0).toUpperCase() + s.slice(1)}`)}
                </button>
              ))}
            </div>
            <div className="text-sm text-gray-600 sm:ml-auto" data-testid="expenses-visible-count">
              {t('expenses.totalLabel')}: <span className="font-bold text-gray-900" data-testid="expenses-total">${total.toFixed(2)}</span>
              <span className="text-gray-400 ml-2">({visibleExpenses.length})</span>
            </div>
          </div>

          <Card className="bg-white border-gray-100 shadow-sm overflow-hidden">
            <CardContent className="p-0">
              {loading ? (
                <div className="p-12 text-center text-gray-400">{t('common.loading')}</div>
              ) : visibleExpenses.length === 0 ? (
                <div className="py-16 text-center">
                  <Wallet className="w-12 h-12 mx-auto mb-3 text-gray-300" />
                  <p className="text-gray-500 font-medium">{t('expenses.noExpenses')}</p>
                </div>
              ) : (
                <div className="overflow-x-auto">
                  <table className="w-full">
                    <thead>
                      <tr className="bg-gray-50/80 border-b border-gray-100">
                        <SortHeader label={t('expenses.date')} sortKey="date" activeKey={sortKey} dir={sortDir} onSort={handleSort} className="text-left" testId="expense-sort-date" />
                        <th className="text-left text-xs uppercase tracking-wider text-gray-500 font-semibold px-4 py-3">{t('expenses.type')}</th>
                        <th className="text-left text-xs uppercase tracking-wider text-gray-500 font-semibold px-4 py-3">{t('expenses.description')}</th>
                        <SortHeader label={t('expenses.beneficiary')} sortKey="beneficiary" activeKey={sortKey} dir={sortDir} onSort={handleSort} className="text-left" testId="expense-sort-beneficiary" />
                        <th className="text-left text-xs uppercase tracking-wider text-gray-500 font-semibold px-4 py-3 hidden md:table-cell">{t('expenses.loadedBy')}</th>
                        <SortHeader label={t('expenses.project')} sortKey="project" activeKey={sortKey} dir={sortDir} onSort={handleSort} className="text-left" testId="expense-sort-project" />
                        <th className="text-left text-xs uppercase tracking-wider text-gray-500 font-semibold px-4 py-3 hidden lg:table-cell">{t('expenses.item')}</th>
                        <SortHeader label={t('expenses.status')} sortKey="status" activeKey={sortKey} dir={sortDir} onSort={handleSort} className="text-left" testId="expense-sort-status" />
                        <SortHeader label={t('expenses.amount')} sortKey="amount" activeKey={sortKey} dir={sortDir} onSort={handleSort} className="text-right" testId="expense-sort-amount" />
                        <th className="text-right text-xs uppercase tracking-wider text-gray-500 font-semibold px-4 py-3">{t('common.actions')}</th>
                      </tr>
                    </thead>
                    <tbody>
                      {visibleExpenses.map(e => (
                        <tr key={e.id} data-testid={`expense-row-${e.id}`} className="border-b border-gray-50 last:border-0 hover:bg-gray-50/50 transition-colors">
                          <td className="px-4 py-3 text-sm text-gray-600">{(e.date || '').slice(0, 10)}</td>
                          <td className="px-4 py-3">
                            <span className={`text-xs px-2 py-0.5 rounded-md font-medium capitalize ${TYPE_COLORS[e.expense_type] || TYPE_COLORS.other}`}>{typeLabel(e.expense_type)}</span>
                          </td>
                          <td className="px-4 py-3 text-sm text-gray-700 max-w-[220px] truncate">{e.description || '—'}</td>
                          <td className="px-4 py-3 text-sm text-gray-700">
                            {e.beneficiary_name || '—'}
                            {e.beneficiary_contact_person && (
                              <p className="text-xs text-gray-400" data-testid={`expense-row-${e.id}-contact-person`}>{t('expenses.contactPerson', { name: e.beneficiary_contact_person })}</p>
                            )}
                          </td>
                          <td className="px-4 py-3 text-sm text-gray-500 hidden md:table-cell" data-testid={`expense-row-${e.id}-created-by`}>{e.created_by_name || '—'}</td>
                          <td className="px-4 py-3">
                            {e.project_id ? (
                              <button onClick={() => navigate(`/projects/${e.project_id}`)} className="text-sm text-blue-600 hover:underline flex items-center gap-1">
                                {e.project_number || t('expenses.project')}<ExternalLink className="w-3 h-3" />
                              </button>
                            ) : '—'}
                            {e.project_title && <p className="text-xs text-gray-400 truncate max-w-[160px]">{e.project_title}</p>}
                          </td>
                          <td className="px-4 py-3 text-sm text-gray-500 hidden lg:table-cell max-w-[160px] truncate">{e.item_index !== null && e.item_index !== undefined ? (e.item_description || `#${e.item_index + 1}`) : '—'}</td>
                          <td className="px-4 py-3">
                            <span className={`text-xs px-2 py-0.5 rounded-md font-semibold ${e.payment_status === 'paid' ? 'bg-emerald-50 text-emerald-700' : e.payment_status === 'unpaid' ? 'bg-amber-50 text-amber-700' : 'bg-gray-100 text-gray-500'}`} data-testid={`expense-row-${e.id}-status`}>
                              {e.payment_status === 'paid' ? t('expenses.paid') : e.payment_status === 'unpaid' ? t('expenses.unpaid') : t('expenses.notSet')}
                            </span>
                          </td>
                          <td className="px-4 py-3 text-right text-sm font-semibold text-gray-900">${(e.amount || 0).toFixed(2)}</td>
                          <td className="px-4 py-3 text-right">
                            <DropdownMenu>
                              <DropdownMenuTrigger asChild>
                                <Button variant="ghost" size="icon" data-testid={`expense-actions-${e.id}`} className="h-8 w-8"><MoreHorizontal className="w-4 h-4" /></Button>
                              </DropdownMenuTrigger>
                              <DropdownMenuContent align="end">
                                {canEdit && <DropdownMenuItem onClick={() => openEdit(e)}><Pencil className="w-3.5 h-3.5 mr-2" />{t('common.edit')}</DropdownMenuItem>}
                                {canDelete && <DropdownMenuItem onClick={() => handleDelete(e.id)} className="text-red-600"><Trash2 className="w-3.5 h-3.5 mr-2" />{t('common.delete')}</DropdownMenuItem>}
                              </DropdownMenuContent>
                            </DropdownMenu>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </CardContent>
          </Card>
        </>
      ) : (
        <div className="space-y-3" data-testid="expenses-by-beneficiary-view">
          <div className="flex flex-wrap gap-3">
            <div className="flex-1 min-w-[180px] bg-white border border-gray-200 rounded-xl p-4" data-testid="beneficiary-kpi-total">
              <p className="text-xs font-semibold text-gray-500 uppercase tracking-wide">{t('expenses.totalAll')}</p>
              <p className="text-2xl font-bold text-gray-900 mt-1">${byBeneficiary.total.toFixed(2)}</p>
            </div>
            <div className="flex-1 min-w-[180px] bg-emerald-50 border border-emerald-200 rounded-xl p-4">
              <p className="text-xs font-semibold text-emerald-700 uppercase tracking-wide">{t('expenses.totalPaidLabel')}</p>
              <p className="text-2xl font-bold text-emerald-800 mt-1" data-testid="expenses-total-paid">${byBeneficiary.total_paid.toFixed(2)}</p>
            </div>
            <div className="flex-1 min-w-[180px] bg-amber-50 border border-amber-200 rounded-xl p-4">
              <p className="text-xs font-semibold text-amber-700 uppercase tracking-wide">{t('expenses.totalOwed')}</p>
              <p className="text-2xl font-bold text-amber-800 mt-1" data-testid="expenses-total-owed">${byBeneficiary.total_unpaid.toFixed(2)}</p>
            </div>
            <div className="flex-1 min-w-[180px] bg-gray-50 border border-gray-200 rounded-xl p-4" data-testid="beneficiary-kpi-unset">
              <p className="text-xs font-semibold text-gray-500 uppercase tracking-wide">{t('expenses.totalNotSet')}</p>
              <p className="text-2xl font-bold text-gray-600 mt-1">${byBeneficiary.total_unset.toFixed(2)}</p>
            </div>
          </div>

          <Card className="bg-white border-gray-100 shadow-sm overflow-hidden">
            <CardContent className="p-0">
              {byBeneficiaryLoading ? (
                <div className="p-12 text-center text-gray-400">{t('common.loading')}</div>
              ) : byBeneficiary.beneficiaries.length === 0 ? (
                <div className="py-16 text-center">
                  <Users className="w-12 h-12 mx-auto mb-3 text-gray-300" />
                  <p className="text-gray-500 font-medium">{t('expenses.noBeneficiaryData')}</p>
                </div>
              ) : (
                <div className="overflow-x-auto">
                  <table className="w-full">
                    <thead>
                      <tr className="bg-gray-50/80 border-b border-gray-100">
                        <th className="text-left text-xs uppercase tracking-wider text-gray-500 font-semibold px-4 py-3">{t('expenses.beneficiary')}</th>
                        <th className="text-right text-xs uppercase tracking-wider text-gray-500 font-semibold px-4 py-3">{t('expenses.totalPaidLabel')}</th>
                        <th className="text-right text-xs uppercase tracking-wider text-gray-500 font-semibold px-4 py-3">{t('expenses.totalOwed')}</th>
                        <th className="text-right text-xs uppercase tracking-wider text-gray-500 font-semibold px-4 py-3 hidden md:table-cell">{t('expenses.totalNotSet')}</th>
                        <th className="text-right text-xs uppercase tracking-wider text-gray-500 font-semibold px-4 py-3">{t('expenses.totalAll')}</th>
                        <th className="text-right text-xs uppercase tracking-wider text-gray-500 font-semibold px-4 py-3 hidden sm:table-cell">{t('common.total', { defaultValue: 'Count' })}</th>
                      </tr>
                    </thead>
                    <tbody>
                      {byBeneficiary.beneficiaries.map(b => (
                        <tr key={b.beneficiary_name} data-testid={`beneficiary-row-${b.beneficiary_name}`} className="border-b border-gray-50 last:border-0 hover:bg-gray-50/50 transition-colors">
                          <td className="px-4 py-3 text-sm font-medium text-gray-800">{b.beneficiary_name}</td>
                          <td className="px-4 py-3 text-right text-sm text-emerald-700">${b.paid_total.toFixed(2)}</td>
                          <td className={`px-4 py-3 text-right text-sm font-semibold ${b.unpaid_total > 0 ? 'text-amber-700' : 'text-gray-400'}`}>${b.unpaid_total.toFixed(2)}</td>
                          <td className="px-4 py-3 text-right text-sm text-gray-500 hidden md:table-cell">${b.unset_total.toFixed(2)}</td>
                          <td className="px-4 py-3 text-right">
                            <button type="button" onClick={() => setDetailBeneficiary(b.beneficiary_name)} data-testid={`beneficiary-row-${b.beneficiary_name}-total-btn`}
                              className="text-sm font-bold text-blue-700 hover:underline" title={t('expenses.clickForDetail')}>
                              ${b.total.toFixed(2)}
                            </button>
                          </td>
                          <td className="px-4 py-3 text-right text-sm text-gray-500 hidden sm:table-cell">{b.count}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </CardContent>
          </Card>
        </div>
      )}

      <ExpenseDialog open={dialogOpen} onClose={() => setDialogOpen(false)} onSaved={handleSaved} editing={editing} />
      <BeneficiaryDetailDialog open={!!detailBeneficiary} onClose={() => setDetailBeneficiary(null)} beneficiaryName={detailBeneficiary} />
    </div>
  );
}
